import { useAppDispatch, useAppSelector } from "helpers/hooks";
import { useEffect, useState } from "react";
import { getCompanyInfo, resetUpdateCompany, resetLogoUpdate } from "store/slices/employer/settingsTs/company-info-slice";
import { CircularProgress, Typography, TableBody, TableCell, TableHead, Box, Link, Collapse } from "@mui/material";
import { getPaymentHistory } from "store/slices/employer/create-job/create-job-payments-slice";
import { ShPaper } from "shared/SharedStyles/ShSurfaces";
import { ShChip, ShTable, ShTableContainer, ShTableRow } from "shared/SharedStyles/ShDataDisplay";
import { IPaymentHistoryResponse } from "Modules/Core/CreateJob/CreateJob/CreateJobModel";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PendingIcon from '@mui/icons-material/Pending';
import ReplayIcon from '@mui/icons-material/Replay';
import { toTitleCase } from "shared/utils";

export const PaymentHistory = () => {
    const dispatch = useAppDispatch();
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    
    const { companyInfo } = useAppSelector((state) => state.employer.employerSettings.companyInfo);
    const { paymentHistory, getPaymentHistoryStatus } = useAppSelector((state) => state.employer.createJob.createJobPayments);

    const handleRowClick = (paymentId: string) => {
        setExpandedRow(expandedRow === paymentId ? null : paymentId);
    };

    useEffect(() => {
        if (!companyInfo.id) {
            dispatch(getCompanyInfo());
        }

        if (paymentHistory?.length === 0 && companyInfo.stripe_customer_id) {
            dispatch(getPaymentHistory({ stripe_customer_id: companyInfo.stripe_customer_id }));
        }

        return () => {
            dispatch(resetUpdateCompany());
            dispatch(resetLogoUpdate());
        };
    }, [companyInfo, dispatch, paymentHistory?.length]);

    return (
        <ShPaper variant="outlined">
            <Typography variant="h6" gutterBottom>
                Payment History
            </Typography>

            {getPaymentHistoryStatus === "pending" && (
                <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                    <CircularProgress />
                </Box>
            )}

            {getPaymentHistoryStatus === "failed" && (
                <Typography color="error" align="center">
                    Failed to fetch payment history.
                </Typography>
            )}

            {getPaymentHistoryStatus === "success" && paymentHistory?.length > 0 ? (
                <ShTableContainer>
                    <ShTable>
                        <TableHead>
                            <ShTableRow cursor="default">
                                <TableCell><strong>Date</strong></TableCell>
                                <TableCell><strong>ID</strong></TableCell>
                                <TableCell><strong>Amount</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Invoice</strong></TableCell>
                                <TableCell><strong>Created At</strong></TableCell>
                            </ShTableRow>
                        </TableHead>
                        <TableBody>
                            {paymentHistory.map((payment: IPaymentHistoryResponse) => (
                                <>
                                    <ShTableRow 
                                        key={payment.id} 
                                        cursor="pointer"
                                        onClick={() => handleRowClick(payment.id)}
                                        hover
                                    >
                                        <TableCell>{new Date(payment.createdAt).toLocaleDateString()}</TableCell>
                                        <TableCell>{payment.id}</TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography>
                                                    ${payment.amount} {payment.currency}
                                                </Typography>
                                                {payment.refunds && payment.refunds.length > 0 && (
                                                    <Box>
                                                        {payment.refunds.map((refund, index) => (
                                                            <Box key={refund.id} display="flex" alignItems="center" gap={1}>
                                                                <Typography variant="caption" color="error" gutterBottom>
                                                                    Refunded: -${refund.amount} {refund.currency}
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    ({new Date(refund.createdAt).toLocaleDateString()})
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" gap={1} alignItems="center">
                                                <ShChip 
                                                    hideBoxShadow
                                                    size='small'
                                                    customSize="small"
                                                    variant="outlined"
                                                    icon={
                                                        payment.status === "successful" ? 
                                                            <CheckCircleOutlineIcon color="success" /> : 
                                                        payment.status === "failed" ? 
                                                            <ErrorOutlineIcon color="error" /> :
                                                            <PendingIcon color="warning" />
                                                    }
                                                    label={toTitleCase(payment.status)} 
                                                    borderRadius='25px'
                                                />
                                                {payment.refunds && payment.refunds.length > 0 && (
                                                    <ShChip 
                                                        hideBoxShadow
                                                        size='small'
                                                        customSize="small"
                                                        borderRadius='25px'
                                                        variant="outlined"
                                                        icon={<ReplayIcon color="error" />}
                                                        label="Refunded" 
                                                        textColor="black"
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Link href={payment.invoiceUrl} target="_blank" rel="noopener" color="primary">
                                                View Invoice
                                            </Link>
                                        </TableCell>
                                        <TableCell>{new Date(payment.createdAt).toLocaleString()}</TableCell>
                                    </ShTableRow>
                                    <ShTableRow cursor="default">
                                        <TableCell colSpan={6} style={{ padding: 0 }}>
                                            <Collapse in={expandedRow === payment.id}>
                                                <Box p={2} bgcolor="background.default">
                                                    <Box display="flex" gap={4}>
                                                        <Box flex={1}>
                                                            <Typography variant="subtitle2" gutterBottom>
                                                                Payment Details
                                                            </Typography>
                                                            <Box display="flex" flexDirection="column" gap={1}>
                                                                <Typography variant="body2">
                                                                    <strong>Payment ID:</strong> {payment.id}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    <strong>Amount:</strong> ${payment.amount} {payment.currency}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    <strong>Status:</strong> {toTitleCase(payment.status)}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    <strong>Created:</strong> {new Date(payment.createdAt).toLocaleString()}
                                                                </Typography>
                                                                {payment.updatedAt && (
                                                                    <Typography variant="body2">
                                                                        <strong>Last Updated:</strong> {new Date(payment.updatedAt).toLocaleString()}
                                                                    </Typography>
                                                                )}
                                                                {payment.refunds && payment.refunds.length > 0 && (
                                                                    <Box mt={1}>
                                                                        <Typography variant="subtitle2" gutterBottom>
                                                                            Refund Details
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            <strong>Amount:</strong> -${payment.refunds[0].amount} {payment.refunds[0].currency}
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            <strong>Date:</strong> {new Date(payment.refunds[0].createdAt).toLocaleString()}
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            <strong>Status:</strong> {toTitleCase(payment.refunds[0].status)}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                        {payment.invoiceUrl && (
                                                            <Box flex={2}>
                                                                <Typography variant="subtitle2" gutterBottom>
                                                                    Invoice
                                                                </Typography>
                                                                <Link
                                                                    href={payment.invoiceUrl}
                                                                    target="_blank"
                                                                    rel="noopener"
                                                                    color="primary"
                                                                    underline="always"
                                                                    sx={{ fontWeight: 600, fontSize: 16 }}
                                                                >
                                                                    View Invoice (opens in new tab)
                                                                </Link>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </ShTableRow>
                                </>
                            ))}
                        </TableBody>
                    </ShTable>
                </ShTableContainer>
            ) : (
                getPaymentHistoryStatus === "success" && (
                    <Typography align="center" color="textSecondary">
                        No payment history available.
                    </Typography>
                )
            )}
        </ShPaper>
    );
};

export default PaymentHistory;
